import { supabase } from "@/integrations/supabase/client";
import type { DocumentHistoryScope } from "@/domains/documents/history/documentHistoryShared";

interface ScopedDocumentRow {
  id: number;
  filename: string;
  file_path: string | null;
  content_type: string | null;
  size: number | null;
  version: number | null;
  version_group: string | null;
  is_deleted: boolean | null;
  state: string | null;
}

export type ScopedDocumentIndex = Map<number, ScopedDocumentRow>;

/**
 * How many in-scope document ids may be inlined into the audit query's
 * `entity_id=in.(…)` filter. PostgREST reads filters off the request URL, so
 * the list has to stay well inside the gateway's URL limit — a thousand numeric
 * ids is roughly 8KB.
 *
 * A busy building exceeds this routinely — the scope counts every version of
 * every document, not just the lineage heads — so treat the overflow path as
 * the normal one, not an edge case. There the event query is organisation-wide
 * and each batch's documents are resolved after the fact: still correct, and no
 * longer able to stall the page (the merge emits everything above the audit
 * source's watermark, so a page never comes back empty with "Load more" lit),
 * but the delete/restore rows for this building surface a batch at a time as
 * the reader pages. Narrowing this properly needs the join server-side — an
 * `event_logs`⋈`documents` RPC in franklin-data-pipeline. It cannot be done
 * from here: `event_logs.entity_id` is text, so a min/max id range would be
 * compared lexicographically ("9" > "1000"), and `metadata->>buildingId` is
 * written from a row the writer may not hold.
 *
 * It must also stay strictly below PostgREST's `max_rows` cap (1000 here, as
 * `useTemplateFormDocuments` documents), because the overflow probe asks for
 * one row more than the limit. At 1000 the server would truncate the response
 * to exactly 1000 rows, the probe could never see 1001, and a scope holding
 * more documents than the cap would be treated as complete — narrowing the
 * audit query to an arbitrary (unordered) 1000 ids and silently dropping every
 * delete/restore event belonging to the rest.
 */
export const SCOPED_DOCUMENT_INDEX_LIMIT = 500;

function scopedDocumentsQuery(scope: DocumentHistoryScope) {
  const query = supabase
    .from("documents")
    .select(
      "id, filename, file_path, content_type, size, version, version_group, is_deleted, state"
    )
    .eq("org_id", scope.orgId)
    .eq("building_id", scope.buildingId)
    .eq("hidden", false)
    // Match `fetchCreatedDocuments`: a generated DWG child PDF is not a file a
    // user ever sees, and trashing a DWG cascades to it — resolving the child's
    // audit event too would render two "Moved to Trash" rows for one action,
    // one of them for a document that can never have an "Added" row.
    //
    // `is_latest_version` is deliberately NOT matched here. That flag is
    // re-elected on every `is_deleted` change, so a trashed document is no
    // longer its lineage's head; filtering on it would drop the delete event
    // for the very document being deleted, which is what this source exists to
    // surface. It describes the lineage now, not at the time of the event.
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");

  return scope.projectId === null ? query : query.eq("project_id", scope.projectId);
}

function indexRows(data: unknown): ScopedDocumentIndex {
  const rows = (data as unknown as ScopedDocumentRow[]) ?? [];
  return new Map(rows.map((row) => [row.id, row]));
}

export async function fetchScopedDocuments(
  scope: DocumentHistoryScope,
  documentIds: number[]
): Promise<ScopedDocumentIndex> {
  if (documentIds.length === 0) return new Map();

  const { data, error } = await scopedDocumentsQuery(scope).in("id", documentIds);
  if (error) throw error;

  return indexRows(data);
}

/**
 * Every document the scope contains, or null when there are more than the URL
 * filter can carry. Resolved once per page and handed to the audit source so
 * its event query is narrowed server-side instead of scanning the whole
 * organisation and discarding what belongs to other buildings.
 */
export async function fetchScopedDocumentIndex(
  scope: DocumentHistoryScope
): Promise<ScopedDocumentIndex | null> {
  const { data, error } = await scopedDocumentsQuery(scope).limit(SCOPED_DOCUMENT_INDEX_LIMIT + 1);
  if (error) throw error;

  const index = indexRows(data);
  return index.size > SCOPED_DOCUMENT_INDEX_LIMIT ? null : index;
}
